/**
 * Audio theme — Web Audio singleton player + persistence helpers.
 *
 * Mirrors the visual theme split: pure utility module here, React layer
 * lives in `src/store/audio-theme-context.tsx`. Components should call
 * `playCue(...)` directly; the context syncs settings to this module so
 * non-React callers (hooks, run-event handlers) work too.
 *
 * Implementation notes:
 *  - Uses Web Audio API for low-latency, overlapping cue playback.
 *  - Browser autoplay policy: AudioContext can't start until a user
 *    gesture. We lazily create it on first `unlock()` call (wired by the
 *    provider to a one-shot `pointerdown`/`keydown` listener).
 *  - Cue buffers are decoded once and cached. Prefetch on unlock.
 *  - SSR / jsdom-safe: every entry point no-ops when `window` or
 *    `AudioContext` is unavailable.
 */

import {
  type AudioCue,
  type AudioVariant,
  audioRegistry,
  allCues,
} from '../audio/registry';

export const AUDIO_ENABLED_KEY = 'audio-theme-enabled';
export const AUDIO_VOLUME_KEY = 'audio-theme-volume';
export const AUDIO_VARIANT_KEY = 'audio-theme-variant';

export const AUDIO_DEFAULT_ENABLED = true;
export const AUDIO_DEFAULT_VOLUME = 0.5;
export const AUDIO_DEFAULT_VARIANT: AudioVariant = 'default';

// ─── Persistence ────────────────────────────────────────────────────────

export function getStoredAudioEnabled(): boolean {
  if (typeof localStorage === 'undefined') return AUDIO_DEFAULT_ENABLED;
  const stored = localStorage.getItem(AUDIO_ENABLED_KEY);
  if (stored === 'true') return true;
  if (stored === 'false') return false;
  return AUDIO_DEFAULT_ENABLED;
}

export function storeAudioEnabled(enabled: boolean): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(AUDIO_ENABLED_KEY, enabled ? 'true' : 'false');
}

export function getStoredAudioVolume(): number {
  if (typeof localStorage === 'undefined') return AUDIO_DEFAULT_VOLUME;
  const raw = localStorage.getItem(AUDIO_VOLUME_KEY);
  if (raw == null) return AUDIO_DEFAULT_VOLUME;
  const n = Number(raw);
  if (!Number.isFinite(n)) return AUDIO_DEFAULT_VOLUME;
  return Math.min(1, Math.max(0, n));
}

export function storeAudioVolume(volume: number): void {
  if (typeof localStorage === 'undefined') return;
  const clamped = Math.min(1, Math.max(0, volume));
  localStorage.setItem(AUDIO_VOLUME_KEY, String(clamped));
}

export function getStoredAudioVariant(): AudioVariant {
  if (typeof localStorage === 'undefined') return AUDIO_DEFAULT_VARIANT;
  const stored = localStorage.getItem(AUDIO_VARIANT_KEY);
  if (stored && stored in audioRegistry) return stored as AudioVariant;
  return AUDIO_DEFAULT_VARIANT;
}

export function storeAudioVariant(variant: AudioVariant): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(AUDIO_VARIANT_KEY, variant);
}

// ─── Singleton player ───────────────────────────────────────────────────

interface AudioPlayerSettings {
  enabled: boolean;
  volume: number;
  variant: AudioVariant;
}

const settings: AudioPlayerSettings = {
  enabled: getStoredAudioEnabled(),
  volume: getStoredAudioVolume(),
  variant: getStoredAudioVariant(),
};

let ctx: AudioContext | null = null;
let masterGain: GainNode | null = null;
const bufferCache = new Map<string, AudioBuffer>();
const inFlight = new Map<string, Promise<AudioBuffer | null>>();
let unlocked = false;

type AudioContextCtor = typeof AudioContext;

function resolveAudioContextCtor(): AudioContextCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

// ── Hidden-tab audio suspension ─────────────────────────────────────────
// Safari's CoreAudio resume path can wedge a backgrounded page that holds a
// RUNNING AudioContext (part of the 2026-08 black-frozen-tab incident). Park
// the shared context while the tab is hidden and resume on return — unless a
// realtime voice session is live (`setAudioKeepAliveWhileHidden(true)`),
// where playback must continue in the background.
let keepAliveWhileHidden = false;
let suspendedByVisibility = false;
let visibilityWired = false;

export function setAudioKeepAliveWhileHidden(on: boolean): void {
  keepAliveWhileHidden = on;
}

function wireVisibilitySuspend(): void {
  if (visibilityWired || typeof document === 'undefined') return;
  visibilityWired = true;
  document.addEventListener('visibilitychange', () => {
    if (!ctx) return;
    if (document.hidden) {
      if (!keepAliveWhileHidden && ctx.state === 'running') {
        suspendedByVisibility = true;
        ctx.suspend().catch(() => { /* best-effort */ });
      }
    } else if (suspendedByVisibility && ctx.state === 'suspended') {
      suspendedByVisibility = false;
      ctx.resume().catch(() => { /* best-effort */ });
    }
  });
}

function ensureContext(): AudioContext | null {
  if (ctx) return ctx;
  const Ctor = resolveAudioContextCtor();
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
    masterGain = ctx.createGain();
    masterGain.gain.value = settings.volume;
    masterGain.connect(ctx.destination);
    wireVisibilitySuspend();
    return ctx;
  } catch {
    ctx = null;
    masterGain = null;
    return null;
  }
}

/**
 * Shared audio graph for non-cue playback (voice PCM streaming). Routing
 * through `masterGain` keeps the user's volume setting applied. Returns null
 * when Web Audio is unavailable (SSR / jsdom). The caller is responsible for
 * only playing after a user gesture (a suspended context stays silent).
 */
export function getAudioGraph(): { ctx: AudioContext; masterGain: GainNode } | null {
  const audioCtx = ensureContext();
  if (!audioCtx || !masterGain) return null;
  return { ctx: audioCtx, masterGain };
}

/** Current AudioContext state without creating one — diagnostics only. */
export function peekAudioContextState(): string {
  return ctx ? ctx.state : 'not-created';
}

/**
 * Create AND resume the shared context synchronously within a user gesture.
 * Safari refuses `resume()` outside a gesture handler (Chrome's sticky
 * activation is laxer), so every voice entry point MUST call this in its
 * onClick — before any `await` — or playback stays silently suspended.
 * Differs from `unlockAudio()` in that it always re-attempts resume (that
 * one is one-shot) and skips the cue prefetch.
 */
export function unlockAudioPlayback(): void {
  const audioCtx = ensureContext();
  if (!audioCtx) return;
  if (audioCtx.state === 'suspended') {
    audioCtx.resume().catch(() => { /* best-effort */ });
  }
}

async function loadBuffer(url: string): Promise<AudioBuffer | null> {
  if (bufferCache.has(url)) return bufferCache.get(url)!;
  const existing = inFlight.get(url);
  if (existing) return existing;
  const audioCtx = ensureContext();
  if (!audioCtx) return null;

  const promise = (async () => {
    try {
      const res = await fetch(url);
      if (!res.ok) return null;
      const arr = await res.arrayBuffer();
      const buf = await audioCtx.decodeAudioData(arr.slice(0));
      bufferCache.set(url, buf);
      return buf;
    } catch {
      return null;
    } finally {
      inFlight.delete(url);
    }
  })();

  inFlight.set(url, promise);
  return promise;
}

function resolveCueUrl(cue: AudioCue, variant: AudioVariant): string | null {
  const variantMap = audioRegistry[variant] ?? audioRegistry[AUDIO_DEFAULT_VARIANT];
  return variantMap[cue] ?? null;
}

/**
 * Unlock audio playback after a user gesture. Idempotent — safe to wire to
 * `pointerdown`/`keydown` listeners; only the first call does work.
 * Resumes a suspended context (Safari's autoplay quirk) and pre-decodes
 * cue buffers for the active variant.
 */
export function unlockAudio(): void {
  if (unlocked) return;
  const audioCtx = ensureContext();
  if (!audioCtx) return;
  unlocked = true;
  if (audioCtx.state === 'suspended') {
    audioCtx.resume().catch(() => { /* best-effort */ });
  }
  prefetchVariant(settings.variant);
}

/**
 * Eagerly load every cue buffer for `variant`. Called by `unlockAudio`
 * after the first user gesture, and by the context when the variant
 * changes.
 */
export function prefetchVariant(variant: AudioVariant): void {
  const audioCtx = ensureContext();
  if (!audioCtx) return;
  for (const cue of allCues) {
    const url = resolveCueUrl(cue, variant);
    if (url) void loadBuffer(url);
  }
}

export function setAudioEnabled(enabled: boolean): void {
  settings.enabled = enabled;
}

export function setAudioVolume(volume: number): void {
  const clamped = Math.min(1, Math.max(0, volume));
  settings.volume = clamped;
  if (masterGain) masterGain.gain.value = clamped;
}

export function setAudioVariant(variant: AudioVariant): void {
  settings.variant = variant;
  prefetchVariant(variant);
}

export function getAudioSettings(): Readonly<AudioPlayerSettings> {
  return settings;
}

interface PlayCueOptions {
  /** 0..1 attenuation applied on top of the master volume. */
  volume?: number;
  /** Override variant (mostly for previewing in Settings). */
  variant?: AudioVariant;
  /** Force-play even when settings.enabled is false (Settings preview). */
  force?: boolean;
}

/**
 * Play a registered audio cue. No-op when audio is disabled or the
 * AudioContext can't be created. Multiple overlapping plays are supported
 * — each call creates a fresh BufferSource.
 *
 * Safe to call before the AudioContext has been unlocked: the function
 * awaits `resume()` (which is async — synchronously checking `state`
 * after calling it sees the pre-resume value) and falls through to play
 * once running. If no user gesture has happened yet (so `resume()` keeps
 * the context suspended), the cue is silently dropped.
 */
export function playCue(cue: AudioCue, opts: PlayCueOptions = {}): void {
  if (!opts.force && !settings.enabled) return;
  const audioCtx = ensureContext();
  if (!audioCtx || !masterGain) return;

  const variant = opts.variant ?? settings.variant;
  const url = resolveCueUrl(cue, variant);
  if (!url) return;

  void (async () => {
    try {
      if (audioCtx.state === 'suspended') {
        await audioCtx.resume();
      }
      // Re-check enabled after the async hop in case settings flipped off.
      if (!opts.force && !settings.enabled) return;
      // Browser autoplay policy still in effect — no gesture yet.
      if (audioCtx.state !== 'running') return;

      const buf = bufferCache.get(url) ?? await loadBuffer(url);
      if (!buf) return;
      if (!opts.force && !settings.enabled) return;
      if (audioCtx.state !== 'running' || !masterGain) return;
      playBuffer(audioCtx, masterGain, buf, opts.volume);
    } catch {
      // Web Audio errors are non-critical — never crash the UI for a missed cue.
    }
  })();
}

const onceFiredKeys = new Set<string>();

/**
 * Play a cue at most once for a given dedupe key. Useful for terminal
 * events that may surface through multiple paths (e.g. the run-events
 * hook plus the composer adapter). The Set is bounded — it trims itself
 * after a soft cap to avoid unbounded growth across long sessions.
 */
export function playCueOnce(
  cue: AudioCue,
  key: string,
  opts?: PlayCueOptions,
): void {
  if (onceFiredKeys.has(key)) return;
  onceFiredKeys.add(key);
  if (onceFiredKeys.size > 1000) {
    // Trim oldest half to keep the set bounded.
    const arr = Array.from(onceFiredKeys);
    onceFiredKeys.clear();
    for (const k of arr.slice(arr.length / 2)) onceFiredKeys.add(k);
  }
  playCue(cue, opts);
}

function playBuffer(
  audioCtx: AudioContext,
  destination: GainNode,
  buffer: AudioBuffer,
  cueVolume: number | undefined,
): void {
  try {
    const src = audioCtx.createBufferSource();
    src.buffer = buffer;
    if (cueVolume != null && cueVolume !== 1) {
      const cueGain = audioCtx.createGain();
      cueGain.gain.value = Math.min(1, Math.max(0, cueVolume));
      src.connect(cueGain);
      cueGain.connect(destination);
    } else {
      src.connect(destination);
    }
    src.start(0);
  } catch {
    // Audio playback errors are non-critical.
  }
}
